import React from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { getMonthName } from '../../../../tools/formatter';
import UrlManager from '../../../../tools/UrlManager';
import './Breadcrumb.scss';

function Breadcrumb(props) {
    const { userId } = useParams();

    // Determine current level based on what's selected
    const getCurrentLevel = () => {
        if (props.currentNavigationEventId) return 'event';
        if (props.currentNavigationDay) return 'day';
        if (props.currentNavigationMonth) return 'month';
        if (props.currentNavigationYear) return 'year';
        return 'username';
    };

    // Display text functions for each navigation level with null/undefined checks
    
    const getYearDisplay = () => {
        return props.currentNavigationYear ?? 'select year';
    };

    const getMonthDisplay = () => {
        // Check for null/undefined and validate month number range
        if (props.currentNavigationMonth !== null && 
            props.currentNavigationMonth !== undefined) {
            const monthNum = parseInt(props.currentNavigationMonth, 10);
            // Validate month is in valid range (1-12)
            if (monthNum >= 1 && monthNum <= 12) {
                try {
                    return getMonthName(window.navigator.language, props.currentNavigationMonth);
                } catch (error) {
                    // If formatting fails, fall back to default text
                    return 'select month';
                }
            }
        }
        return 'select month';
    };

    const getDayDisplay = () => {
        return props.currentNavigationDay ?? 'select day';
    };

    const getEventDisplay = () => {
        // Use optional chaining and nullish coalescing for robust null/undefined handling
        return props.currentEvent?.title ?? 'select event';
    };

    // Navigation handlers - update Redux state immediately and then update URL
    
    const handleUsernameClick = () => {
        // Navigate to root - reset all levels
        props.onUpdateCurrentNavigationYear(null);
        props.onUpdateCurrentNavigationMonth(null);
        props.onUpdateCurrentNavigationDay(null);
        props.onUpdateCurrentNavigationEventId(null);
        UrlManager.setUrl(userId);
    };

    const handleYearClick = () => {
        // Navigate to year view - reset month, day, and event
        if (props.currentNavigationYear) {
            props.onUpdateCurrentNavigationMonth(null);
            props.onUpdateCurrentNavigationDay(null);
            props.onUpdateCurrentNavigationEventId(null);
            UrlManager.setUrl(userId, props.currentNavigationYear);
        }
    };

    const handleMonthClick = () => {
        // Navigate to month view - reset day and event
        if (props.currentNavigationMonth) {
            props.onUpdateCurrentNavigationDay(null);
            props.onUpdateCurrentNavigationEventId(null);
            UrlManager.setUrl(userId, props.currentNavigationYear, props.currentNavigationMonth);
        }
    };

    const handleDayClick = () => {
        // Navigate to day view - reset event
        if (props.currentNavigationDay) {
            props.onUpdateCurrentNavigationEventId(null);
            UrlManager.setUrl(userId, props.currentNavigationYear, props.currentNavigationMonth, props.currentNavigationDay);
        }
    };

    const currentLevel = getCurrentLevel();

    // Helper function to determine if a segment should be clickable
    const isClickable = (level, hasValue) => {
        return hasValue && level !== currentLevel;
    };

    // Helper function to get CSS class for a segment based on its state
    const getSegmentClass = (level, hasValue) => {
        const baseClass = 'breadcrumb-segment';
        
        // Current level: bold and non-clickable
        if (level === currentLevel) {
            return `${baseClass} breadcrumb-segment--current`;
        }
        
        // Selected non-current: clickable with underline and pointer cursor
        if (hasValue) {
            return `${baseClass} breadcrumb-segment--clickable`;
        }
        
        // Unselected: gray and italic placeholder
        return `${baseClass} breadcrumb-segment--placeholder`;
    };

    return (
        <div className="breadcrumb-container">
            <span 
                className={getSegmentClass('username', true)}
                onClick={currentLevel !== 'username' ? handleUsernameClick : undefined}
            >
                {userId || 'User'}
            </span>
            
            {/* Only show year and beyond if we're at username level or year is selected */}
            <>
                <span className="breadcrumb-separator"> / </span>
                <span 
                    className={getSegmentClass('year', props.currentNavigationYear)}
                    onClick={isClickable('year', props.currentNavigationYear) ? handleYearClick : undefined}
                >
                    {getYearDisplay()}
                </span>
            </>
            
            {/* Only show month and beyond if year is selected */}
            {props.currentNavigationYear && (
                <>
                    <span className="breadcrumb-separator"> / </span>
                    <span 
                        className={getSegmentClass('month', props.currentNavigationMonth)}
                        onClick={isClickable('month', props.currentNavigationMonth) ? handleMonthClick : undefined}
                    >
                        {getMonthDisplay()}
                    </span>
                </>
            )}
            
            {/* Only show day and beyond if month is selected */}
            {props.currentNavigationYear && props.currentNavigationMonth && (
                <>
                    <span className="breadcrumb-separator"> / </span>
                    <span 
                        className={getSegmentClass('day', props.currentNavigationDay)}
                        onClick={isClickable('day', props.currentNavigationDay) ? handleDayClick : undefined}
                    >
                        {getDayDisplay()}
                    </span>
                </>
            )}
            
            {/* Only show event if day is selected */}
            {props.currentNavigationYear && props.currentNavigationMonth && props.currentNavigationDay && (
                <>
                    <span className="breadcrumb-separator"> / </span>
                    <span 
                        className={getSegmentClass('event', props.currentNavigationEventId)}
                    >
                        {getEventDisplay()}
                    </span>
                </>
            )}
        </div>
    );
}

const mapStateToProps = state => {
    return {
        currentNavigationYear: state.eventsData.currentNavigationYear,
        currentNavigationMonth: state.eventsData.currentNavigationMonth,
        currentNavigationDay: state.eventsData.currentNavigationDay,
        currentNavigationEventId: state.eventsData.currentNavigationEventId,
        currentEvent: state.eventsData.currentEvent
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onUpdateCurrentNavigationYear: (currentNavigationYear) => dispatch({ type: 'UPDATE_NAVIGATION_YEAR', currentNavigationYear }),
        onUpdateCurrentNavigationMonth: (currentNavigationMonth) => dispatch({ type: 'UPDATE_NAVIGATION_MONTH', currentNavigationMonth }),
        onUpdateCurrentNavigationDay: (currentNavigationDay) => dispatch({ type: 'UPDATE_NAVIGATION_DAY', currentNavigationDay }),
        onUpdateCurrentNavigationEventId: (currentNavigationEventId) => dispatch({ type: 'UPDATE_NAVIGATION_EVENT_ID', currentNavigationEventId })
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Breadcrumb);
